import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { keyedPath } from '@/shared/api/keyedPath';

/**
 * Mirrors {@code UsrSetting} on the BE -- one row of {@code dbxx.ph10_usr_setting}.
 */
export interface UsrSetting {
  usrId: string;
  warehouseId: string | null;
  category: string;
  settingKey: string;
  settingValue: string | null;
  updatedAt: string;
}

const BASE = '/api/users/settings';

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v.length > 0) sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function listKey(category: string) {
  return ['settings', 'list', category] as const;
}

function effKey(category: string, key: string, warehouseId?: string) {
  return ['settings', 'effective', category, key, warehouseId ?? null] as const;
}

/**
 * Fetch every setting in a category for the current user -- both the global
 * row (warehouse_id IS NULL) and any per-warehouse rows. Used by the
 * print-dialog open hook to pre-populate everything in one call.
 */
export function useSettingsList(category: string) {
  return useQuery<UsrSetting[]>({
    queryKey: listKey(category),
    queryFn: () => api<UsrSetting[]>(BASE + qs({ category })),
    staleTime: 30_000,
  });
}

/**
 * "Effective" value lookup with global fallback. Returns the value string or
 * null if absent. {@code warehouseId} optional -- pass it when the
 * setting is warehouse-scoped (printers); omit for user-global settings.
 *
 * <p>Returns null (not undefined) for the "no setting" case so TanStack
 * Query is happy -- the library treats {@code undefined} as a special
 * "no data yet" sentinel and warns when a queryFn returns it.
 */
export function useEffectiveSetting(
  category: string,
  key: string,
  warehouseId?: string,
) {
  return useQuery<string | null>({
    queryKey: effKey(category, key, warehouseId),
    queryFn: async () => {
      try {
        const res = await api<{ value: string | null }>(
          keyedPath('/api/users/settings/{category}/{key}/effective', { category, key }) +
            qs({ warehouseId }),
        );
        return res.value ?? null;
      } catch (e) {
        if (e instanceof Error && 'status' in e && (e as { status?: number }).status === 404) {
          return null;
        }
        throw e;
      }
    },
    staleTime: 30_000,
  });
}

/**
 * Upsert one setting. {@code warehouseId} optional -- omit for global rows.
 * Invalidates the matching list + effective queries on success so callers
 * see the new value immediately.
 */
export function useUpsertSetting() {
  const qc = useQueryClient();
  return useMutation<
    UsrSetting,
    Error,
    { category: string; key: string; warehouseId?: string; value: string | null }
  >({
    mutationFn: ({ category, key, warehouseId, value }) =>
      api<UsrSetting>(
        keyedPath('/api/users/settings/{category}/{key}', { category, key }) +
          qs({ warehouseId }),
        { method: 'PUT', json: { warehouseId, value } },
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['settings', 'list', variables.category] });
      void qc.invalidateQueries({
        queryKey: ['settings', 'effective', variables.category, variables.key],
      });
    },
  });
}

/**
 * Delete one setting row (revert to default). Same invalidation behavior
 * as {@link useUpsertSetting}.
 */
export function useDeleteSetting() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { category: string; key: string; warehouseId?: string }
  >({
    mutationFn: ({ category, key, warehouseId }) =>
      api<void>(
        keyedPath('/api/users/settings/{category}/{key}', { category, key }) +
          qs({ warehouseId }),
        { method: 'DELETE' },
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['settings', 'list', variables.category] });
      void qc.invalidateQueries({
        queryKey: ['settings', 'effective', variables.category, variables.key],
      });
    },
  });
}
